import { AGE_LABELS } from "../data/eventOptions";

/*
 * Alias conservé pour ne pas casser les composants existants.
 */
export const AGE_CATEGORIES = AGE_LABELS;

export const WEEK_DAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const PARIS_DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  timeZone: "Europe/Paris",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export function toCanonicalCategory(token) {
  const value = normalize(token);

  if (value.includes("eveil")) {
    return "Éveil";
  }

  if (value.includes("mini") && value.includes("poussin")) {
    return "Mini-Poussin(e)";
  }

  if (!value.includes("mini") && value.includes("poussin")) {
    return "Poussin(e)";
  }

  if (value.includes("benjamin")) {
    return "Benjamin(e)";
  }

  if (value.includes("minime")) {
    return "Minime";
  }

  if (value.includes("cadet")) {
    return "Cadet(te)";
  }

  if (value.includes("junior")) {
    return "Junior";
  }

  if (value.includes("senior")) {
    return "Sénior";
  }

  if (value.includes("veteran")) {
    return "Vétéran";
  }

  if (value.includes("adult")) {
    return "ADULT";
  }

  if (value.includes("u-21")) {
    return "U-21";
  }

  if (value.includes("u-18")) {
    return "U-18";
  }

  if (value.includes("u-16")) {
    return "U-16";
  }

  if (value.includes("u-14")) {
    return "U-14";
  }

  if (value.includes("u-12")) {
    return "U-12";
  }

  if (value.includes("u-10")) {
    return "U-10";
  }

  if (value.includes("u-8")) {
    return "U-8";
  }

  return null;
}

export function extractEventCategories(cateEvent) {
  const values = Array.isArray(cateEvent)
    ? cateEvent
    : String(cateEvent || "").split(/[,/|;]+/g);

  const categorySet = new Set();

  values
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .forEach((value) => {
      const category = toCanonicalCategory(value);

      if (category) {
        categorySet.add(category);
      }
    });

  return AGE_LABELS.filter((category) => categorySet.has(category));
}

export function getSeasonLabelFromDate(dateInput = new Date()) {
  const date = new Date(dateInput);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const year = date.getFullYear();

  /*
   * La saison commence le 1er août
   * et se termine le 31 juillet suivant.
   */
  const seasonStartYear = date.getMonth() >= 7 ? year : year - 1;

  return `${seasonStartYear}-${seasonStartYear + 1}`;
}

export function getCurrentSeasonLabel() {
  return getSeasonLabelFromDate(new Date());
}

export function toDateKey(value) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = PARIS_DATE_FORMATTER.formatToParts(date);

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  if (!year || !month || !day) {
    return null;
  }

  return `${year}-${month}-${day}`;
}

export function dateKeyToDate(dateKey) {
  if (!dateKey) return null;

  const [year, month, day] = String(dateKey)
    .split("-")
    .map((value) => Number(value));

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function startOfMonth(dateInput = new Date()) {
  const date = new Date(dateInput);

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    1,
    12,
    0,
    0,
    0
  );
}

export function shiftMonth(dateInput, amount) {
  const date = startOfMonth(dateInput);

  return new Date(
    date.getFullYear(),
    date.getMonth() + amount,
    1,
    12,
    0,
    0,
    0
  );
}

export function buildMonthGridDays(monthDate) {
  const firstDay = startOfMonth(monthDate);

  /*
   * getDay :
   * dimanche = 0
   * lundi = 1
   *
   * Transformation :
   * lundi = 0
   * dimanche = 6
   */
  const mondayOffset = (firstDay.getDay() + 6) % 7;

  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);

    date.setDate(gridStart.getDate() + index);
    date.setHours(12, 0, 0, 0);

    return date;
  });
}

export function getEventDateRange(event) {
  const startKey = toDateKey(event?.start);

  if (!startKey) {
    return {
      startKey: null,
      endKey: null,
    };
  }

  const rawEndKey = toDateKey(event?.end);

  const endKey =
    rawEndKey && rawEndKey >= startKey
      ? rawEndKey
      : startKey;

  return {
    startKey,
    endKey,
  };
}

export function eventOccursOnDate(event, dateKey) {
  if (!dateKey) return false;

  const { startKey, endKey } = getEventDateRange(event);

  if (!startKey || !endKey) {
    return false;
  }

  return dateKey >= startKey && dateKey <= endKey;
}

export function eventIntersectsMonth(event, monthDate) {
  const monthStart = startOfMonth(monthDate);

  const monthEnd = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0,
    12,
    0,
    0,
    0
  );

  const monthStartKey = toDateKey(monthStart);
  const monthEndKey = toDateKey(monthEnd);

  const { startKey, endKey } = getEventDateRange(event);

  if (!startKey || !endKey) {
    return false;
  }

  return startKey <= monthEndKey && endKey >= monthStartKey;
}

export function getEventsForMonth(events = [], monthDate) {
  return events
    .filter((event) => eventIntersectsMonth(event, monthDate))
    .sort(sortEventsByStartDate);
}

export function buildEventsByDate(events = [], days = []) {
  const result = {};

  days.forEach((day) => {
    const dateKey = toDateKey(day);

    result[dateKey] = events
      .filter((event) => eventOccursOnDate(event, dateKey))
      .sort(sortEventsByStartDate);
  });

  return result;
}

export function sortEventsByStartDate(firstEvent, secondEvent) {
  const firstDate = new Date(firstEvent?.start).getTime();
  const secondDate = new Date(secondEvent?.start).getTime();

  const validFirstDate = Number.isNaN(firstDate)
    ? Number.MAX_SAFE_INTEGER
    : firstDate;

  const validSecondDate = Number.isNaN(secondDate)
    ? Number.MAX_SAFE_INTEGER
    : secondDate;

  if (validFirstDate !== validSecondDate) {
    return validFirstDate - validSecondDate;
  }

  return String(firstEvent?.titre || "").localeCompare(
    String(secondEvent?.titre || ""),
    "fr"
  );
}

export function formatMonthTitle(monthDate) {
  const label = new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
  }).format(monthDate);

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatDayLong(date) {
  const label = new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatEventTime(event) {
  if (event?.horaire_event) {
    return String(event.horaire_event);
  }

  if (!event?.start) {
    return "";
  }

  const rawValue = String(event.start);

  /*
   * Une date simple comme 2026-07-15
   * ne doit pas afficher 02:00.
   */
  const containsTime = /[T\s]\d{2}:\d{2}/.test(rawValue);

  if (!containsTime) {
    return "";
  }

  const date = new Date(event.start);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function isToday(date) {
  return toDateKey(date) === toDateKey(new Date());
}

export function isPastDay(date) {
  const dateKey = toDateKey(date);
  const todayKey = toDateKey(new Date());

  return Boolean(
    dateKey &&
      todayKey &&
      dateKey < todayKey
  );
}

export function isSameMonth(date, monthDate) {
  return (
    date.getFullYear() === monthDate.getFullYear() &&
    date.getMonth() === monthDate.getMonth()
  );
}